"use strict";

import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ADD_USERS_TO_TEAM,
    CREATE_TEAM,
    GET_USERS,
    REMOVE_USERS_FROM_TEAM,
    UPDATE_TEAM_NAME,
} from "../ApiEndpoints";
import UserSelectAutocomplete from "../Components/UserSelectAutoComplete";
import { Store } from "../Store";
import { fetchManagedTeams } from "../utils/teamRequests";

const TeamFormScreen = ({ mode }) => {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const navigate = useNavigate();
    const { teamId } = useParams();
    const isEditMode = mode === "edit";

    const [teamName, setTeamName] = useState("");
    const [projectManagers, setProjectManagers] = useState([]);
    const [members, setMembers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [loadingTeam, setLoadingTeam] = useState(isEditMode);
    const [userNameById, setUserNameById] = useState({});
    const hasInitializedDefaultRef = useRef(false);
    const originalMembersRef = useRef([]);
    const originalTeamNameRef = useRef("");

    const fallbackProjectManager = useMemo(() => {
        if (isEditMode) {
            return null;
        }
        if (!userInfo?.id) {
            return null;
        }
        return {
            id: userInfo.id,
            name: userInfo.name || userInfo.email || userInfo.id,
            email: userInfo.email,
        };
    }, [isEditMode, userInfo?.email, userInfo?.id, userInfo?.name]);
    const resolvedProjectManagers = useMemo(
        () => (projectManagers.length > 0 ? projectManagers : []),
        [projectManagers]
    );

    const selectedProjectManagerLabel = useMemo(() => {
        if (resolvedProjectManagers.length > 0) {
            const labels = resolvedProjectManagers.map((manager) => {
                const details = manager?.name || manager?.email || manager?.id;
                const identifier = manager?.email || manager?.id;
                if (identifier && details !== identifier) {
                    return `${details} (${identifier})`;
                }
                return details;
            });
            return labels.filter(Boolean).join(", ");
        }
        if (!isEditMode && userInfo?.name) {
            return `${userInfo.name} (${userInfo.email || userInfo.id})`;
        }
        return !isEditMode ? userInfo?.email || userInfo?.id || "" : "";
    }, [resolvedProjectManagers, userInfo, isEditMode]);

    const selectedMembersLabel = useMemo(() => {
        if (members.length === 0) {
            return "";
        }
        const labels = members.map((member) => {
            const details = member?.name || member?.email || member?.id;
            const identifier = member?.email || member?.id;
            if (identifier && details !== identifier) {
                return `${details} (${identifier})`;
            }
            return details;
        });
        return labels.filter(Boolean).join(", ");
    }, [members]);

    const selectedProjectManagerCount = useMemo(
        () => projectManagers.filter((manager) => manager?.id).length,
        [projectManagers]
    );
    const canAddProjectManager = !isEditMode && selectedProjectManagerCount < 1;

    const handleProjectManagerUpdate = useCallback((index, user) => {
        setProjectManagers((prev) => {
            const next = [...prev];
            next[index] = user;
            return next;
        });
    }, []);

    const handleMemberUpdate = useCallback((index, user) => {
        setMembers((prev) => {
            const next = [...prev];
            next[index] = user;
            return next;
        });
    }, []);

    const handleAddProjectManager = () => {
        setProjectManagers((prev) => [...prev, null]);
    };

    const handleAddMember = () => {
        setMembers((prev) => [...prev, null]);
    };

    const handleRemoveProjectManager = (index) => {
        setProjectManagers((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    };

    const handleRemoveMember = (index) => {
        setMembers((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    };

    const fetchUsersForLookup = useCallback(async () => {
        try {
            // Request a full user list so we can label the current project manager.
            const response = await axios.post(GET_USERS, {
                page: 1,
                search: "",
                pageSize: 1000,
            });
            // Build an ID-to-name lookup for quick display updates.
            const usersData = Array.isArray(response.data?.users)
                ? response.data.users
                : [];
            const lookup = {};
            usersData.forEach((user) => {
                if (user?.id) {
                    lookup[user.id] = user.name || user.email || user.id;
                }
            });
            setUserNameById(lookup);
        } catch (err) {
            console.error("Error fetching users for team editing:", err);
            setUserNameById({});
        }
    }, []);

    const hydrateTeamState = useCallback((team, lookup) => {
        const projectManagers = Array.isArray(team?.project_manager)
            ? team.project_manager
            : team?.project_manager
                ? [team.project_manager]
                : [];
        const projectManagerId = projectManagers[0] || "";
        const members = Array.isArray(team?.members) ? team.members : [];
        const managerName = lookup?.[projectManagerId];
        const hydratedProjectManagers = projectManagers.map((managerId) => ({
            id: managerId,
            name: lookup?.[managerId] || managerId,
            email: "",
        }));
        const hydratedMembers = members.map((memberId) => ({
            id: memberId,
            name: lookup?.[memberId] || memberId,
            email: "",
        }));

        setTeamName(team?.team_name || "");
        originalTeamNameRef.current = team?.team_name || "";
        originalMembersRef.current = members;
        setMembers(hydratedMembers);
        setProjectManagers(
            hydratedProjectManagers.length > 0
                ? hydratedProjectManagers
                : projectManagerId
                    ? [
                        {
                            id: projectManagerId,
                            name: managerName || projectManagerId,
                            email: "",
                        },
                    ]
                    : []
        );
    }, []);

    const fetchTeam = useCallback(async () => {
        if (!userInfo?.id || !teamId) {
            setLoadingTeam(false);
            return;
        }

        try {
            // Request the teams list and locate the team matching the route ID.
            setLoadingTeam(true);
            const teamsData = await fetchManagedTeams({ requesterUserId: userInfo.id });
            const matchingTeam = teamsData.find((team) => {
                const teamKey = team.team_id || team.id || team.team_name;
                return String(teamKey) === String(teamId);
            });

            if (!matchingTeam) {
                setError("Unable to find that team.");
                return;
            }

            // Hydrate the form with the team details for editing.
            hydrateTeamState(matchingTeam, userNameById);
            setError(null);
        } catch (err) {
            console.error("Error loading team for edit:", err);
            setError("Failed to load team details.");
        } finally {
            setLoadingTeam(false);
        }
    }, [hydrateTeamState, teamId, userInfo?.id, userNameById]);

    useEffect(() => {
        if (isEditMode) {
            fetchUsersForLookup();
        }
    }, [fetchUsersForLookup, isEditMode]);

    useEffect(() => {
        if (isEditMode || hasInitializedDefaultRef.current) {
            return;
        }
        // Seed the default project manager for new teams once user info is available.
        if (fallbackProjectManager) {
            setProjectManagers([fallbackProjectManager]);
        }
        hasInitializedDefaultRef.current = true;
    }, [fallbackProjectManager, isEditMode]);

    useEffect(() => {
        if (isEditMode) {
            fetchTeam();
        }
    }, [fetchTeam, isEditMode]);

    useEffect(() => {
        if (!isEditMode || Object.keys(userNameById).length === 0) {
            return;
        }

        setProjectManagers((prev) =>
            prev.map((manager) => {
                if (!manager?.id) {
                    return manager;
                }
                const updatedName = userNameById[manager.id];
                if (updatedName && manager.name === manager.id) {
                    return {
                        ...manager,
                        name: updatedName,
                    };
                }
                return manager;
            })
        );
        setMembers((prev) =>
            prev.map((member) => {
                if (!member?.id) {
                    return member;
                }
                const updatedName = userNameById[member.id];
                if (updatedName && member.name === member.id) {
                    return {
                        ...member,
                        name: updatedName,
                    };
                }
                return member;
            })
        );
    }, [isEditMode, userNameById]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmedTeamName = teamName.trim();

        // Validate required fields before attempting to save.
        if (!trimmedTeamName) {
            setError("Team name is required.");
            return;
        }

        if (selectedProjectManagerCount === 0) {
            setError("Select at least one project manager before continuing.");
            return;
        }

        if (selectedProjectManagerCount > 1) {
            setError("Only one project manager can be selected for now.");
            return;
        }

        // Normalize and deduplicate the member selection list.
        const memberUids = members
            .map((member) => member?.id)
            .filter(Boolean);
        const uniqueMemberUids = Array.from(new Set(memberUids));
        const projectManagerUids = resolvedProjectManagers
            .map((manager) => manager?.id)
            .filter(Boolean);
        const primaryProjectManagerId = projectManagerUids[0] || "";

        try {
            // Submit the create or update request based on the current mode.
            setSubmitting(true);
            setError(null);

            if (isEditMode) {
                const originalMembers = Array.isArray(originalMembersRef.current)
                    ? originalMembersRef.current
                    : [];
                const originalTeamName = originalTeamNameRef.current || "";
                const originalMemberSet = new Set(originalMembers);
                const updatedMemberSet = new Set(uniqueMemberUids);
                const membersToAdd = uniqueMemberUids.filter((uid) => !originalMemberSet.has(uid));
                const membersToRemove = originalMembers.filter((uid) => !updatedMemberSet.has(uid));
                const shouldUpdateTeamName = trimmedTeamName !== originalTeamName;

                if (shouldUpdateTeamName) {
                    await axios.post(UPDATE_TEAM_NAME, {
                        requester_user_id: userInfo?.id,
                        team_id: teamId,
                        team_name: trimmedTeamName,
                    });
                    originalTeamNameRef.current = trimmedTeamName;
                }

                if (membersToAdd.length > 0) {
                    await axios.post(ADD_USERS_TO_TEAM, {
                        requester_user_id: userInfo?.id,
                        team_id: teamId,
                        member_uids: membersToAdd,
                    });
                }

                if (membersToRemove.length > 0) {
                    await axios.post(REMOVE_USERS_FROM_TEAM, {
                        requester_user_id: userInfo?.id,
                        team_id: teamId,
                        member_uids: membersToRemove,
                    });
                }
            } else {
                await axios.post(CREATE_TEAM, {
                    requester_user_id: userInfo?.id,
                    project_manager_user_id: primaryProjectManagerId,
                    team_name: trimmedTeamName,
                    member_uids: uniqueMemberUids,
                });
            }

            // Return to the list filtered by the edited team name.
            navigate(`/admin/teams?search=${encodeURIComponent(trimmedTeamName)}`);
        } catch (err) {
            console.error("Error saving team:", err);
            setError(
                isEditMode
                    ? "Failed to update team. Please try again."
                    : "Failed to create team. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loadingTeam) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: "50vh" }}
            >
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid item container justifyContent="center">
            <Grid
                item
                container
                xs={7}
                marginTop={7}
                direction="column"
                component="form"
                onSubmit={handleSubmit}
                gap={3}
            >
                <Grid item>
                    <Typography
                        sx={{
                            fontSize: "var(--font-size-header)",
                            fontWeight: 600,
                        }}
                    >
                        {isEditMode ? "Edit Team" : "Create Team"}
                    </Typography>
                </Grid>
                <Grid item>
                    <TextField
                        label="Team name"
                        value={teamName}
                        onChange={(event) => setTeamName(event.target.value)}
                        fullWidth
                        required
                        disabled={submitting}
                    />
                </Grid>
                <Grid item>
                    <Typography variant="subtitle2" gutterBottom>
                        Project manager
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={6}>
                            <Grid container direction="column" gap={2}>
                                {projectManagers.map((manager, index) => (
                                    <Grid item key={`pm-${index}`}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item xs>
                                                <UserSelectAutocomplete
                                                    onUserSelect={(user) => handleProjectManagerUpdate(index, user)}
                                                    selectedUser={manager}
                                                    fetchUsersUrl={GET_USERS}
                                                    applyWidth={false}
                                                    disabled={isEditMode || submitting}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <IconButton
                                                    aria-label="Remove project manager"
                                                    onClick={() => handleRemoveProjectManager(index)}
                                                    disabled={isEditMode || submitting}
                                                >
                                                    <RemoveCircleOutlineIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}
                                <Grid item>
                                    <Button
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={handleAddProjectManager}
                                        disabled={!canAddProjectManager || submitting}
                                    >
                                        Add project manager
                                    </Button>
                                </Grid>
                            </Grid>
                            {selectedProjectManagerLabel && (
                                <Typography variant="caption" color="text.secondary">
                                    Selected: {selectedProjectManagerLabel}
                                </Typography>
                            )}
                            {isEditMode && (
                                <Typography variant="caption" color="text.secondary">
                                    Project manager updates are not yet supported.
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" gutterBottom>
                                Members
                            </Typography>
                            <Grid container direction="column" gap={2}>
                                {members.map((member, index) => (
                                    <Grid item key={`member-${index}`}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item xs>
                                                <UserSelectAutocomplete
                                                    onUserSelect={(user) => handleMemberUpdate(index, user)}
                                                    selectedUser={member}
                                                    fetchUsersUrl={GET_USERS}
                                                    applyWidth={false}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <IconButton
                                                    aria-label="Remove member"
                                                    onClick={() => handleRemoveMember(index)}
                                                    disabled={submitting}
                                                >
                                                    <RemoveCircleOutlineIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                ))}
                                <Grid item>
                                    <Button
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={handleAddMember}
                                        disabled={submitting}
                                    >
                                        Add member
                                    </Button>
                                </Grid>
                            </Grid>
                            {selectedMembersLabel && (
                                <Typography variant="caption" color="text.secondary">
                                    Selected: {selectedMembersLabel}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                {error && (
                    <Grid item>
                        <Typography color="error">{error}</Typography>
                    </Grid>
                )}
                <Grid item container spacing={2} alignItems="center">
                    <Grid item>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                isEditMode ? "Save Changes" : "Create Team"
                            )}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/admin/teams")}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TeamFormScreen;
